/**
 * Workaround oficial para el error de navegación de Ionic React v6
 * "Cannot read properties of undefined (reading 'classList')"
 * 
 * Basado en las soluciones recomendadas por el equipo de Ionic:
 * - https://github.com/ionic-team/ionic-framework/issues/24332
 * - https://github.com/ionic-team/ionic-framework/issues/26524
 */

/**
 * Aplica un parche para prevenir errores de classList undefined
 * durante las transiciones de navegación de Ionic
 */
export const applyIonicNavigationFix = (): void => {
  // Parche para elementos DOM que pueden no tener classList
  const originalAddEventListener = EventTarget.prototype.addEventListener;
  
  EventTarget.prototype.addEventListener = function(type, listener, options) {
    if (type === 'ionRouteWillChange' || type === 'ionRouteDidChange') {
      const wrappedListener = (event: any) => {
        try {
          if (typeof listener === 'function') {
            listener.call(this, event);
          } else if (listener && typeof listener.handleEvent === 'function') {
            listener.handleEvent(event);
          }
        } catch (error) {
          // Suprimir errores específicos de classList durante navegación
          if (error instanceof TypeError && 
              error.message.includes('classList') && 
              error.message.includes('undefined')) {
            console.warn('Ionic navigation classList error suppressed:', error.message);
            return;
          }
          throw error;
        }
      };
      
      return originalAddEventListener.call(this, type, wrappedListener, options);
    }
    
    return originalAddEventListener.call(this, type, listener, options);
  };
};

/**
 * Maneja errores específicos de navegación de Ionic
 */
export const handleNavigationError = (error: Error): boolean => {
  const errorMessage = error.message || '';
  
  // Lista de errores conocidos de navegación de Ionic que se pueden suprimir
  const knownNavigationErrors = [
    'Cannot read properties of undefined (reading \'classList\')',
    'Cannot read property \'classList\' of undefined',
    'Cannot read properties of null (reading \'classList\')',
    'Cannot read property \'classList\' of null'
  ];
  
  const isKnownNavigationError = knownNavigationErrors.some(knownError => 
    errorMessage.includes(knownError)
  );
  
  if (isKnownNavigationError) {
    console.warn('Ionic navigation error suppressed:', errorMessage);
    return true; // Error manejado
  }
  
  return false; // Error no manejado
};

/**
 * Inicializa el fix de navegación de Ionic
 * Debe llamarse una vez al inicio de la aplicación
 */
export const initializeNavigationFix = (): void => {
  // Aplicar parche para classList
  applyIonicNavigationFix();
  
  // Manejar errores no capturados
  window.addEventListener('error', (event) => {
    if (handleNavigationError(event.error)) {
      event.preventDefault();
      event.stopPropagation();
    }
  });
  
  // Manejar promesas rechazadas
  window.addEventListener('unhandledrejection', (event) => {
    if (event.reason instanceof Error && handleNavigationError(event.reason)) {
      event.preventDefault();
    }
  });
  
  console.log('✅ Ionic navigation fix initialized');
};

/**
 * Workaround adicional: Asegurar que los elementos DOM tengan classList
 * antes de las operaciones de navegación
 */
export const ensureElementClassList = (element: any): void => {
  if (element && typeof element === 'object' && !element.classList) {
    // Crear un classList mock si no existe
    element.classList = {
      add: () => {},
      remove: () => {},
      contains: () => false,
      toggle: () => false,
      replace: () => false
    };
  }
};

/**
 * Parche específico para IonRouterOutlet
 * Previene errores durante las transiciones de página
 */
export const patchIonRouterOutlet = (): void => {
  // Esperar a que Ionic esté completamente cargado
  setTimeout(() => {
    const routerOutlets = document.querySelectorAll('ion-router-outlet');
    
    routerOutlets.forEach(outlet => {
      // Asegurar que el outlet tenga las propiedades necesarias
      if (outlet && !outlet.classList) {
        ensureElementClassList(outlet);
      }
      
      // Parche para elementos hijos
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node: any) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              ensureElementClassList(node);
            }
          });
        });
      });
      
      observer.observe(outlet, {
        childList: true,
        subtree: true
      });
    });
  }, 100);
};
